/**
 * A tiny observable store.
 *
 * This is the stand-in for React's Context API in a server-rendered app: a
 * single source of truth that any module can read, write and subscribe to,
 * without those modules needing to know about each other.
 *
 * Deliberately ~40 lines. State here is genuinely small (cart count, wishlist
 * ids, theme), and a full state library would be more ceremony than the
 * problem deserves.
 */

export type Listener<T> = (state: T, previous: T) => void
export type Unsubscribe = () => void

export interface Store<T> {
  get: () => T
  set: (partial: Partial<T> | ((current: T) => Partial<T>)) => void
  subscribe: (listener: Listener<T>) => Unsubscribe
}

export function createStore<T extends object>(initial: T): Store<T> {
  let state = initial
  const listeners = new Set<Listener<T>>()

  return {
    get: () => state,

    set(partial) {
      const previous = state
      const patch = typeof partial === 'function' ? partial(state) : partial
      const next = { ...state, ...patch }

      // Bail out when nothing actually changed — this keeps a chatty caller
      // from triggering a cascade of pointless DOM writes.
      const changed = (Object.keys(patch) as (keyof T)[]).some((key) => previous[key] !== next[key])
      if (!changed) return

      state = next
      listeners.forEach((listener) => {
        listener(state, previous)
      })
    },

    subscribe(listener) {
      listeners.add(listener)
      // Push the current value immediately so subscribers can render without
      // waiting for the first change.
      listener(state, state)

      return () => listeners.delete(listener)
    },
  }
}

/**
 * Persist part of a store to localStorage and rehydrate it on load.
 *
 * Storage access is wrapped in try/catch throughout: Safari private mode and
 * some corporate policies throw on access, and a store failing to persist must
 * never take the page down with it.
 */
export function persist<T extends object>(store: Store<T>, key: string, keys: (keyof T)[]): void {
  try {
    const saved = localStorage.getItem(key)
    if (saved !== null) {
      const parsed = JSON.parse(saved) as Partial<T>
      const allowed: Partial<T> = {}
      keys.forEach((k) => {
        if (k in parsed) allowed[k] = parsed[k]
      })
      store.set(allowed)
    }
  } catch {
    /* Unreadable or corrupt storage — carry on with defaults. */
  }

  store.subscribe((state) => {
    try {
      const slice: Partial<T> = {}
      keys.forEach((k) => {
        slice[k] = state[k]
      })
      localStorage.setItem(key, JSON.stringify(slice))
    } catch {
      /* Quota exceeded or storage blocked — non-fatal. */
    }
  })
}
